/**
 * TemplateFieldsViewer - Componente para visualizar e editar campos do template
 * 
 * Exibe os campos do template na visualização da demanda, permitindo
 * edição de campos marcados como editáveis (ex: data_pagamento no template de pagamento).
 */

import React, { useMemo, useState, useEffect } from 'react'
import { HiPencil, HiCheck, HiXMark, HiCalendar, HiDocumentText } from 'react-icons/hi2'
import { cn, formatarCNPJ } from '../../../utils/formatter'
import { RichTextDisplay } from '../../../components/RichTextDisplay'
import { Textarea } from '../../../components/Textarea'
import { getTemplateById } from '../registry'
import type { TemplateSchema, CampoEditavelConfig } from '../types'
import { getFornecedores, getSetoresEUnidades } from '../services'

interface TemplateRespostas {
  template_id: string
  template_versao: string
  preenchido_em: string
  respostas: Record<string, unknown>
}

interface TemplateFieldsViewerProps {
  /** Dados do template armazenados na demanda */
  templateRespostas: TemplateRespostas
  /** Se está em modo de edição completa (todos os campos editáveis) */
  isEditing?: boolean
  /** Callback quando um campo é alterado (modo edição completa) */
  onFieldChange?: (fieldName: string, value: unknown) => void
  /** Callback quando salvar alterações de campos editáveis (inline) */
  onSaveField?: (fieldName: string, value: unknown) => Promise<void>
  /** Classes CSS adicionais */
  className?: string
  /** Se deve permitir edição inline de campos marcados como editáveis */
  allowInlineEdit?: boolean
  /** Se deve mostrar o header com nome do template */
  showHeader?: boolean
}

/**
 * Formata valor para exibição baseado no tipo do campo
 */
function formatFieldValue(
  value: unknown,
  format?: string,
  fieldName?: string,
  fornecedoresMap?: Map<string, { nome: string; razao_social: string; cnpj: string }>,
  setoresMap?: Map<string, string>
): string {
  if (value === null || value === undefined || value === '') {
    return '—'
  }

  // Se for array, não formatar aqui (será renderizado como componente especial)
  if (Array.isArray(value)) {
    return ''
  }

  // Formatação de boolean
  if (typeof value === 'boolean') {
    return value ? 'Sim' : 'Não'
  }

  // Formatação específica para campos do template RQ36
  if (fieldName === 'contrato_fornecedor' && typeof value === 'string' && fornecedoresMap) {
    const fornecedor = fornecedoresMap.get(value)
    if (fornecedor) {
      return `${fornecedor.nome} - ${fornecedor.razao_social} (${formatarCNPJ(fornecedor.cnpj)})`
    }
    // Fallback para exibir CNPJ formatado se não encontrar
    return formatarCNPJ(value)
  }

  if (fieldName === 'unidade' && typeof value === 'string' && setoresMap) {
    const setorNome = setoresMap.get(value)
    if (setorNome) {
      return setorNome
    }
  }

  // Formatação de data
  if (format === 'date' && typeof value === 'string') {
    try {
      const date = new Date(value + 'T00:00:00')
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    } catch {
      return String(value)
    }
  }

  return String(value)
}

/**
 * Componente para renderizar arrays (como lista de pagamentos)
 */
const ArrayFieldDisplay: React.FC<{
  value: unknown[]
  schema?: { items?: { properties?: Record<string, { title?: string; format?: string }> } }
  isEditable?: boolean
  editableFields?: string[] // Campos específicos que podem ser editados
  onSave?: (newValue: unknown[]) => Promise<void>
}> = ({ value, schema, isEditable = false, editableFields, onSave }) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editValue, setEditValue] = useState<string>('')
  const [isSaving, setIsSaving] = useState(false)
  const [localValue, setLocalValue] = useState(value)

  if (!Array.isArray(localValue) || localValue.length === 0) {
    return <span className="text-gray-400 dark:text-gray-500 italic">Nenhum item</span>
  }

  const itemProperties = schema?.items?.properties || {}
  const propertyKeys = Object.keys(itemProperties)

  // Se não há schema de propriedades, renderizar como lista simples
  if (propertyKeys.length === 0) {
    return (
      <ul className="list-disc list-inside space-y-1">
        {localValue.map((item, index) => (
          <li key={index} className="text-sm text-gray-700 dark:text-gray-300">
            {typeof item === 'object' ? JSON.stringify(item) : String(item)}
          </li>
        ))}
      </ul>
    )
  }

  const handleStartEdit = (index: number, field: string, currentValue: unknown) => {
    // Se há lista de campos editáveis, verificar se este campo pode ser editado
    if (editableFields && !editableFields.includes(field)) {
      return
    }
    setEditingIndex(index)
    setEditingField(field)
    setEditValue(String(currentValue || ''))
  }

  const handleSaveEdit = async () => {
    if (editingIndex === null || editingField === null || !onSave) return

    setIsSaving(true)
    try {
      // Criar nova array com o valor atualizado
      const newArray = localValue.map((item, idx) => {
        if (idx === editingIndex) {
          return {
            ...(item as Record<string, unknown>),
            [editingField]: editValue
          }
        }
        return item
      })

      await onSave(newArray)
      setLocalValue(newArray)
      setEditingIndex(null)
      setEditingField(null)
    } catch (error) {
      console.error('Erro ao salvar:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancelEdit = () => {
    setEditingIndex(null)
    setEditingField(null)
    setEditValue('')
  }

  // Renderizar como tabela
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
          <thead className="bg-gray-50 dark:bg-neutral-800">
            <tr>
              {propertyKeys.map(key => {
                const prop = itemProperties[key] as { title?: string }
                return (
                  <th
                    key={key}
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    {prop.title || key}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-neutral-900 divide-y divide-gray-200 dark:divide-neutral-700">
            {localValue.map((item, index) => (
              <tr key={index} className="group">
                {propertyKeys.map(key => {
                  const isFieldEditable = !editableFields || editableFields.includes(key)
                  const prop = itemProperties[key] as { format?: string }
                  const cellValue = (item as Record<string, unknown>)[key]
                  const isEditing = editingIndex === index && editingField === key

                  return (
                    <td
                      key={key}
                      className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100"
                    >
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <input
                            type={prop.format === 'date' ? 'date' : 'text'}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            disabled={isSaving}
                            className={cn(
                              'px-2 py-1 text-sm rounded border',
                              'bg-white dark:bg-neutral-800',
                              'border-blue-300 dark:border-blue-600',
                              'focus:outline-none focus:ring-1 focus:ring-blue-500'
                            )}
                            autoFocus
                          />
                          <button
                            onClick={handleSaveEdit}
                            disabled={isSaving}
                            className="p-1 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded"
                            title="Salvar"
                          >
                            <HiCheck className="w-4 h-4" />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={isSaving}
                            className="p-1 text-gray-600 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded"
                            title="Cancelar"
                          >
                            <HiXMark className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span>{formatFieldValue(cellValue, prop.format)}</span>
                          {isEditable && isFieldEditable && !isSaving && (
                            <button
                              onClick={() => handleStartEdit(index, key, cellValue)}
                              className={cn(
                                'p-0.5 text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded',
                                'opacity-0 group-hover:opacity-100 transition-opacity'
                              )}
                              title="Editar"
                            >
                              <HiPencil className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * Componente de campo editável inline
 */
const EditableField: React.FC<{
  fieldName: string
  value: unknown
  config: CampoEditavelConfig
  format?: string
  onSave: (value: unknown) => Promise<void>
  fornecedoresMap?: Map<string, { nome: string; razao_social: string; cnpj: string }>
  setoresMap?: Map<string, string>
}> = ({ fieldName, value, config, format, onSave, fornecedoresMap, setoresMap }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value as string || '')
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await onSave(editValue)
      setIsEditing(false)
    } catch (error) {
      console.error('Erro ao salvar campo:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setEditValue(value as string || '')
    setIsEditing(false)
  }

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        {format === 'date' ? (
          <input
            type="date"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className={cn(
              'flex-1 px-3 py-1.5 text-sm rounded-lg border',
              'bg-white dark:bg-neutral-800',
              'border-blue-300 dark:border-blue-600',
              'text-gray-900 dark:text-gray-100',
              'focus:outline-none focus:ring-2 focus:ring-blue-500/20'
            )}
            disabled={isSaving}
          />
        ) : (
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className={cn(
              'flex-1 px-3 py-1.5 text-sm rounded-lg border',
              'bg-white dark:bg-neutral-800',
              'border-blue-300 dark:border-blue-600',
              'text-gray-900 dark:text-gray-100',
              'focus:outline-none focus:ring-2 focus:ring-blue-500/20'
            )}
            disabled={isSaving}
          />
        )}
        <button
          onClick={handleSave}
          disabled={isSaving}
          className={cn(
            'p-1.5 rounded-lg transition-colors',
            'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
            'hover:bg-green-200 dark:hover:bg-green-900/50',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          title="Salvar"
        >
          <HiCheck className="w-4 h-4" />
        </button>
        <button
          onClick={handleCancel}
          disabled={isSaving}
          className={cn(
            'p-1.5 rounded-lg transition-colors',
            'bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-400',
            'hover:bg-gray-200 dark:hover:bg-neutral-600',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          title="Cancelar"
        >
          <HiXMark className="w-4 h-4" />
        </button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2 group">
      <span className="text-gray-900 dark:text-gray-100">
        {formatFieldValue(value, format, fieldName, fornecedoresMap, setoresMap)}
      </span>
      {config.editavel && (
        <button
          onClick={() => setIsEditing(true)}
          className={cn(
            'p-1 rounded transition-all',
            'text-blue-500 dark:text-blue-400',
            'opacity-0 group-hover:opacity-100',
            'hover:bg-blue-100 dark:hover:bg-blue-900/30'
          )}
          title={config.descricao || 'Editar campo'}
        >
          <HiPencil className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  )
}

/**
 * Componente principal para visualização de campos do template
 */
export const TemplateFieldsViewer: React.FC<TemplateFieldsViewerProps> = ({
  templateRespostas,
  isEditing = false,
  onFieldChange,
  onSaveField,
  className,
  allowInlineEdit = true,
  showHeader = true,
}) => {
  // Estado para mapas de dados auxiliares
  const [fornecedoresMap, setFornecedoresMap] = useState<Map<string, { nome: string; razao_social: string; cnpj: string }>>(new Map())
  const [setoresMap, setSetoresMap] = useState<Map<string, string>>(new Map())

  // Carregar dados auxiliares
  useEffect(() => {
    const loadAuxData = async () => {
      try {
        const [fornecedores, setores] = await Promise.all([
          getFornecedores(),
          getSetoresEUnidades()
        ])

        // Criar mapa de fornecedores (CNPJ -> dados completos)
        const fornecMap = new Map()
        fornecedores.forEach(f => {
          if (f.data) {
            fornecMap.set(f.data.cnpj, f.data)
          }
        })
        setFornecedoresMap(fornecMap)

        // Criar mapa de setores (ID -> nome)
        const setMap = new Map()
        setores.forEach(s => {
          setMap.set(s.value, s.label)
        })
        setSetoresMap(setMap)
      } catch (error) {
        console.error('Erro ao carregar dados auxiliares:', error)
      }
    }

    loadAuxData()
  }, [])

  // Buscar definição do template pelo ID
  const templateDefinition = useMemo(() => {
    return getTemplateById(templateRespostas.template_id)
  }, [templateRespostas.template_id])

  // Obter schema do template
  const templateSchema = useMemo<TemplateSchema | null>(() => {
    if (!templateDefinition) return null
    const schema = templateDefinition.getSchema()
    // getSchema pode retornar Promise, mas para templates síncronos retorna direto
    if (schema instanceof Promise) {
      console.warn('Template assíncrono não suportado no TemplateFieldsViewer')
      return null
    }
    return schema
  }, [templateDefinition])

  // Extrair campos editáveis e suas configurações
  const camposEditaveis = templateSchema?.camposEditaveis || {}

  // Extrair propriedades do schema para labels e tipos
  const schemaProperties = useMemo(() => {
    return templateSchema?.schema?.properties || {}
  }, [templateSchema?.schema?.properties])

  // Filtrar campos que não são seções (type !== 'null')
  const fieldsToDisplay = useMemo(() => {
    const respostas = templateRespostas.respostas || {}
    const entries = Object.entries(respostas)
    
    // Se não tem schema, exibir todos os campos
    if (!schemaProperties || Object.keys(schemaProperties).length === 0) {
      return entries
    }
    
    return entries.filter(([key]) => {
      const prop = schemaProperties[key] as { type?: string } | undefined
      // Não exibir campos de seção (type: null)
      return prop?.type !== 'null'
    })
  }, [templateRespostas.respostas, schemaProperties])

  // Se não tem campos para exibir, retornar null
  if (fieldsToDisplay.length === 0) {
    return null
  }

  const handleSaveField = async (fieldName: string, value: unknown) => {
    if (onSaveField) {
      await onSaveField(fieldName, value)
    }
  }

  // Nome do template para exibição
  const templateNome = templateSchema?.template_nome || 'Dados do Formulário'

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header com nome do template - apenas se showHeader=true */}
      {showHeader && (
        <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-neutral-700">
          <HiDocumentText className="w-4 h-4 text-blue-500 dark:text-blue-400" />
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {templateNome}
          </h4>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            v{templateRespostas.template_versao}
          </span>
        </div>
      )}

      {/* Campos do template */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fieldsToDisplay.map(([fieldName, value]) => {
          const prop = schemaProperties[fieldName] as { 
            title?: string
            type?: string
            format?: string
            description?: string
          } | undefined
          
          const fieldConfig = camposEditaveis[fieldName]
          const isFieldInlineEditable = fieldConfig?.editavel && allowInlineEdit && onSaveField
          const label = fieldConfig?.label || prop?.title || fieldName
          const format = prop?.format
          
          // Verificar se é um array
          const isArray = Array.isArray(value)

          // Campos de texto rico ocupam linha inteira
          const isRichText = templateSchema?.uiSchema?.[fieldName]?.['ui:widget'] === 'richtext'
          
          // No modo de edição completa (isEditing), renderizar inputs para todos os campos
          if (isEditing && onFieldChange) {
            if (isRichText) {
              return (
                <div key={fieldName} className="col-span-full space-y-1">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    {label}
                  </label>
                  <Textarea
                    variant="rich"
                    value={value as string || ''}
                    onChange={(newValue) => onFieldChange(fieldName, newValue)}
                    className="min-h-[150px]"
                  />
                </div>
              )
            }
            
            // Arrays não são editáveis no modo de edição completa (use o modo inline)
            if (isArray) {
              return (
                <div key={fieldName} className="col-span-full space-y-1">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    {label}
                  </label>
                  <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                    Arrays não podem ser editados no modo de edição completa. Use o modo de visualização.
                  </div>
                </div>
              )
            }

            return (
              <div key={fieldName} className="space-y-1">
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {format === 'date' && <HiCalendar className="w-3.5 h-3.5" />}
                  {label}
                </label>
                <input
                  type={format === 'date' ? 'date' : 'text'}
                  value={value as string || ''}
                  onChange={(e) => onFieldChange(fieldName, e.target.value)}
                  className={cn(
                    'w-full px-3 py-1.5 text-sm rounded-lg border',
                    'bg-white dark:bg-neutral-800',
                    'border-gray-300 dark:border-neutral-600',
                    'text-gray-900 dark:text-gray-100',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
                    // Fix para data no dark mode
                    format === 'date' && 'dark:[color-scheme:dark]'
                  )}
                />
              </div>
            )
          }
          
          // Modo de visualização - com possibilidade de edição inline para campos específicos
          if (isRichText) {
            return (
              <div key={fieldName} className="col-span-full">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  {label}
                </label>
                <div className="bg-gray-50 dark:bg-neutral-900/50 rounded-lg p-3 border border-gray-200 dark:border-neutral-700">
                  <RichTextDisplay 
                    content={value as string || ''} 
                    className="prose prose-sm max-w-none dark:prose-invert"
                  />
                </div>
              </div>
            )
          }
          
          // Renderizar arrays como tabela
          if (isArray) {
            return (
              <div key={fieldName} className="col-span-full">
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  {label}
                  {isFieldInlineEditable && (
                    <span className="text-blue-500 dark:text-blue-400 normal-case font-normal">
                      (editável)
                    </span>
                  )}
                </label>
                <div className="bg-gray-50 dark:bg-neutral-900/50 rounded-lg p-3 border border-gray-200 dark:border-neutral-700">
                  <ArrayFieldDisplay
                    value={value as unknown[]}
                    schema={prop as { items?: { properties?: Record<string, { title?: string; format?: string }> } }}
                    isEditable={isFieldInlineEditable ? true : false}
                    {...(isFieldInlineEditable && { onSave: (newValue) => handleSaveField(fieldName, newValue) })}
                    {...(fieldName === 'pagamentos' && { editableFields: ['data_pagamento'] })}
                  />
                </div>
              </div>
            )
          }

          return (
            <div key={fieldName} className="space-y-1">
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                {format === 'date' && <HiCalendar className="w-3.5 h-3.5" />}
                {label}
                {isFieldInlineEditable && (
                  <span className="text-blue-500 dark:text-blue-400 normal-case font-normal">
                    (editável)
                  </span>
                )}
              </label>
              
              <div className="text-sm">
                {isFieldInlineEditable ? (
                  <EditableField
                    fieldName={fieldName}
                    value={value}
                    config={fieldConfig!}
                    {...(format !== undefined && { format })}
                    onSave={(newValue) => handleSaveField(fieldName, newValue)}
                    fornecedoresMap={fornecedoresMap}
                    setoresMap={setoresMap}
                  />
                ) : (
                  <span className="text-gray-900 dark:text-gray-100">
                    {formatFieldValue(value, format, fieldName, fornecedoresMap, setoresMap)}
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default TemplateFieldsViewer
